import React, { useState } from 'react';
import { TicketDetail } from '../callCenterAllTicketsTypes';
import { useAuth } from '../../../../contexts/AuthContext';
import styles from './TicketSidebar.module.css';

interface TicketSidebarProps {
  detail: TicketDetail;
  onUpdate?: (input: { status: string }) => Promise<void> | void;
  /** Called with the resolved user id (or null to unassign) when the Assigned-to select changes. */
  onAssign?: (userId: number | null) => Promise<void> | void;
  onEscalate?: () => Promise<void> | void;
  busy?: boolean;
}

const UNASSIGNED = 'unassigned';
const ME = 'me';
const OTHER = 'other';

export const TicketSidebar: React.FC<TicketSidebarProps> = ({ detail, onUpdate, onAssign, onEscalate, busy }) => {
  const { user } = useAuth();
  const [status, setStatus] = useState<string>(detail.status || 'In Progress');

  const currentUserId = user ? parseInt(user.id, 10) : null;
  const isMine = currentUserId != null && detail.assignedToUserId === currentUserId;
  const isUnassigned = detail.assignedToUserId == null;
  // Real value comes from the ticket, not local state — the select always
  // reflects the last-persisted assignment.
  const assignValue = isUnassigned ? UNASSIGNED : isMine ? ME : OTHER;

  const handleAssignChange = (value: string) => {
    if (value === UNASSIGNED) onAssign?.(null);
    else if (value === ME && currentUserId != null) onAssign?.(currentUserId);
    // value === OTHER is a read-only placeholder representing whoever the
    // ticket is currently assigned to; selecting it is a no-op.
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.section}>
        <label className={styles.label}>Assigned to</label>
        <select
          className={styles.select}
          value={assignValue}
          onChange={(e) => handleAssignChange(e.target.value)}
          disabled={busy}
        >
          <option value={UNASSIGNED}>Unassigned</option>
          <option value={ME}>Me{user?.full_name ? ` (${user.full_name})` : ''}</option>
          {assignValue === OTHER && <option value={OTHER}>{detail.assignedToName}</option>}
        </select>
        {assignValue === OTHER && (
          <small className={styles.hint}>
            Reassigning to other staff requires Admin access. You can still take it (“Me”) or unassign it.
          </small>
        )}
      </div>

      <div className={styles.section}>
        <label className={styles.label}>Change Status</label>
        <select
          className={styles.select}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option>Open</option>
          <option>In Progress</option>
          <option>Waiting</option>
          <option>Resolved</option>
          <option>Closed</option>
        </select>
      </div>

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.updateBtn}
          onClick={() => onUpdate?.({ status })}
          disabled={busy}
        >
          {busy ? 'Saving…' : 'Update'}
        </button>
        <button
          type="button"
          className={styles.escalateBtn}
          onClick={() => onEscalate?.()}
          disabled={busy}
        >
          Escalate
        </button>
      </div>

      <div className={styles.infoCard}>
        <h4 className={styles.infoTitle}>Ticket Info</h4>
        <div className={styles.infoRow}>
          <span className={styles.infoLabel}>Module</span>
          <span className={styles.infoValue}>{detail.module}</span>
        </div>
        <div className={styles.infoRow}>
          <span className={styles.infoLabel}>Customer</span>
          <span className={styles.infoValue}>{detail.customer}</span>
        </div>
        <div className={styles.infoRow}>
          <span className={styles.infoLabel}>Reference</span>
          <span className={styles.infoValue}>{detail.reference}</span>
        </div>
        <div className={styles.infoRow}>
          <span className={styles.infoLabel}>Created</span>
          <span className={styles.infoValue}>{detail.created}</span>
        </div>
        <div className={styles.infoRow}>
          <span className={styles.infoLabel}>SLA Due</span>
          <span className={`${styles.infoValue} ${styles.danger}`}>{detail.slaDue}</span>
        </div>
      </div>
    </div>
  );
};
